import type { AxiosResponse } from 'axios'
import type {
  VoucherCouponAbuseResponse,
  VoucherReferral,
  VoucherReferralsListResponse,
  VoucherReferralStatus,
  VoucherReferralUpdatePayload,
} from '@/types/voucherReferral.types'
import { BaseApiService, type QueryParams } from './base.service'

// Ambassador ids per coupon-abuse request, kept well under the endpoint's own
// cap of 200 so the query string stays far from any proxy request-line limit.
const COUPON_ABUSE_CHUNK_SIZE = 100

export class VoucherReferralService extends BaseApiService<VoucherReferral> {
  constructor() {
    super('/voucher-referrals')
  }

  async list(params?: QueryParams): Promise<AxiosResponse<VoucherReferralsListResponse>> {
    return this.client.get(this.collectionUrl, { params })
  }

  async update(
    id: number,
    data: VoucherReferralUpdatePayload,
  ): Promise<AxiosResponse<VoucherReferral>> {
    return this.client.patch(`${this.endpoint}/${id}`, data)
  }

  // How many referrals are currently linked to an ambassador. Used by the edit
  // form to decide whether ambassador fields are editable (only when exclusive).
  async countByAmbassador(ambassadorId: number): Promise<number> {
    const response = await this.list({ ambassador_id: ambassadorId, page: 1, page_size: 1 })
    return response.data.total
  }

  // Which of these ambassadors are abusing their referral coupon (5+ referrals
  // inside a single 7-day window). Computed backend-side over our own history,
  // so it is independent of the Fiscozen backoffice lookups.
  //
  // The ids travel in the query string, and one full export page carries up to
  // 300 of them — enough to approach the ~8 KB request-line limit of the nginx
  // proxy in front of the API, which would answer 414 in HTML. Hence the split
  // into chunks; one failing chunk fails the whole lookup, which the caller
  // degrades to "nobody flagged".
  async couponAbuse(ambassadorIds: number[]): Promise<number[]> {
    const chunks: number[][] = []
    for (let start = 0; start < ambassadorIds.length; start += COUPON_ABUSE_CHUNK_SIZE) {
      chunks.push(ambassadorIds.slice(start, start + COUPON_ABUSE_CHUNK_SIZE))
    }

    const flaggedPerChunk = await Promise.all(chunks.map((chunk) => this.couponAbuseChunk(chunk)))
    return flaggedPerChunk.flat()
  }

  private async couponAbuseChunk(ambassadorIds: number[]): Promise<number[]> {
    const response = await this.client.get<VoucherCouponAbuseResponse>(
      `${this.endpoint}/coupon-abuse`,
      { params: { ambassador_id: ambassadorIds } },
    )
    return response.data.ambassador_ids
  }

  async updateStatus(
    id: number,
    status: VoucherReferralStatus,
  ): Promise<AxiosResponse<VoucherReferral>> {
    return this.client.patch(`${this.endpoint}/${id}/status`, { status })
  }

  async updateNote(id: number, note: string | null): Promise<AxiosResponse<VoucherReferral>> {
    return this.client.patch(`${this.endpoint}/${id}/note`, { note })
  }
}

export const voucherReferralService = new VoucherReferralService()
